import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone,
  Email,
  Language,
  LocationOn,
  LinkedIn,
  DriveEta,
} from "@mui/icons-material";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

// Fixed pixel dimensions for each page size at 96dpi
const PAGE_SIZES = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Full Stack Developer",
  phone: "+91 9876543210",
  email: "johndoe@email.com",
  city: "Chennai, India",
  website: "www.johndoe.com",
  linkedIn: "",
  drivingLicence: "",
  profile:
    "Highly motivated Full Stack Developer with experience in building scalable MERN stack applications. Passionate about problem solving and creating user-friendly solutions.",
  skills: [
    "JavaScript / React",
    "Node.js / Express",
    "MongoDB / MySQL",
    "REST APIs",
    "Teamwork & Communication",
  ],
};

const defaultLeftSectionOrder  = ["contact", "education", "skills"];
const defaultRightSectionOrder = [
  "profile", "workExperience", "languages", "accomplishments",
  "certifications", "softwareSkills", "volunteering", "interests",
  "websites", "references", "additionalInfo",
];

// Common text overflow styles
const overflowText = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  minWidth: 0,
};

const Resume1 = ({
  color          = "#471a33ff",
  font           = "EB Garamond",
  fontSize       = "14px",
  headingSize    = "24px",
  fontStyle      = "normal",
  sectionSpacing   = 50,
  paragraphSpacing = 30,
  lineSpacing      = 20,
  topBottomMargin  = 40,
  sideMargins      = 40,
  lineWeight       = 1,
  pageSize         = "A4",
  formData         = {},
  photo            = "",
  workExperiences  = [],
  exporting        = false,
  enableDragDrop   = true,
}) => {
  const page = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  const data = { ...defaultData, ...formData };

  const SIDEBAR_W = Math.round(page.width * 0.30);
  const MAIN_W    = page.width - SIDEBAR_W;

  const fs      = fontSize   || "14px";
  const hs      = headingSize || "24px";
  const lh      = `${lineSpacing}px`;
  const secGap  = sectionSpacing;
  const paraGap = paragraphSpacing;
  const tbPad   = topBottomMargin;
  const sidePad = sideMargins;

  // ── State ───────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages]             = useState([]);   // [{ rightIds: string[] }]
  const mainContentRef                = useRef(null);   // hidden measure container

  const [educationEntries,     setEducationEntries]     = useState([]);
  const [savedSkills,          setSavedSkills]          = useState([]);
  const [savedSummary,         setSavedSummary]         = useState("");
  const [savedAdditionalInfo,  setSavedAdditionalInfo]  = useState([]);
  const [savedLanguages,       setSavedLanguages]       = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications,  setSavedCertifications]  = useState([]);
  const [savedReferences,      setSavedReferences]      = useState([]);
  const [softwareSkills,       setSoftwareSkills]       = useState([]);
  const [savedVolunteering,    setSavedVolunteering]    = useState([]);
  const [savedInterests,       setSavedInterests]       = useState([]);
  const [savedWebsites,        setSavedWebsites]        = useState([]);
  const [customSections,       setCustomSections]       = useState({});

  const [leftSectionOrder,  setLeftSectionOrder]  = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex,     setDragOverIndex]     = useState(null);
  const [dragOverSide,      setDragOverSide]      = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── LocalStorage ────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const edu = localStorage.getItem("educationEntries");
      if (edu) setEducationEntries(JSON.parse(edu));

      const sk = localStorage.getItem("skills");
      if (sk) setSavedSkills(JSON.parse(sk));

      const sum = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (sum.length) setSavedSummary(sum[sum.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")        || "[]"));
      setSavedLanguages(      JSON.parse(localStorage.getItem("languagesList")         || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")   || "[]"));
      setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList")    || "[]"));
      setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")        || "[]"));
      setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")        || "[]"));
      setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")          || "[]"));
      setSavedInterests(      JSON.parse(localStorage.getItem("interests")             || "[]"));
      setSavedWebsites(       JSON.parse(localStorage.getItem("websites")              || "[]"));

      const userId = localStorage.getItem("userId");
      const sectionsData = {};
      if (userId) {
        const currentCustom = JSON.parse(
          localStorage.getItem(`current_custom_sections_${userId}`) || "[]"
        );
        currentCustom.forEach(name => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${name}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const valid  = (parsed.items || []).filter(i => i && i.trim());
              if (valid.length) sectionsData[name] = valid;
            }
          } catch {}
        });
      }
      setCustomSections(sectionsData);

      const lo = localStorage.getItem("resume1LeftSectionOrder");
      if (lo) {
        const parsed = JSON.parse(lo).filter(s => !s.startsWith("custom_"));
        setLeftSectionOrder(parsed);
      }
      const ro = localStorage.getItem("resume1RightSectionOrder");
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      if (ro) {
        const parsed   = JSON.parse(ro);
        const filtered = parsed.filter(s => !s.startsWith("custom_"));
        setRightSectionOrder([...filtered, ...customIds]);
      } else {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } catch (e) {
      console.error("Resume1 load error:", e);
    }
  }, []);

  const formatDate = (month, year) => (month && year ? `${month} ${year}` : "");

  // ── Drag handlers ───────────────────────────────────────────────────────
  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const handleDragLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = e  => { e.currentTarget.style.opacity = "1"; setDragOverIndex(null); setDragOverSide(null); };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume1LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume1RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null); setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.setItem("resume1LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder));
    localStorage.setItem("resume1RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
  };

  // ── Shared inline style objects ──────────────────────────────────────────

  const sidebarTextStyle = {
    color: "white", fontSize: fs, fontFamily: font, fontStyle, lineHeight: lh, ...overflowText,
  };
  const sidebarHeadStyle = {
    fontSize: hs, fontWeight: "bold", fontFamily: font, color: "white", lineHeight: lh,
    marginBottom: `${Math.round(paraGap * 0.4)}px`, ...overflowText,
  };
  const mainHeadStyle = {
    fontSize: hs, fontWeight: "bold", fontFamily: font, lineHeight: lh,
    marginBottom: `${Math.round(paraGap * 0.4)}px`, ...overflowText,
  };
  const mainTextStyle = {
    fontSize: fs, fontFamily: font, fontStyle, lineHeight: lh, ...overflowText,
  };
  const sectionBottomStyle = { marginBottom: `${secGap}px` };
  const paraBottomStyle    = { marginBottom: `${paraGap}px` };
  const itemGapStyle       = { marginBottom: `${Math.max(4, Math.round(paraGap * 0.25))}px` };

  // ── Left sidebar sections ───────────────────────────────────────────────
  const renderLeftSection = (sectionId) => {
    switch (sectionId) {
      case "contact":
        return (
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Avatar
              src={photo || "https://via.placeholder.com/120"}
              style={{ width: 100, height: 100, marginBottom: `${Math.round(paraGap * 0.5)}px`, border: "3px solid rgba(255,255,255,0.4)" }}
            />
            <Typography style={{ ...sidebarHeadStyle, textAlign: "center" }}>CONTACT</Typography>
            <List dense disablePadding style={{ width: "100%" }}>
              {[
                { show: data.phone,          icon: <Phone    style={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: data.phone },
                { show: data.email,          icon: <Email    style={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: data.email },
                { show: data.city,           icon: <LocationOn style={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: data.city },
                { show: data.website,        icon: <Language style={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: data.website },
                { show: data.linkedIn,       icon: <LinkedIn style={{ fontSize: 14, color: "#90caf9", flexShrink: 0 }} />, val: data.linkedIn },
                { show: data.drivingLicence, icon: <DriveEta style={{ fontSize: 14, color: "white", flexShrink: 0 }} />, val: `DL: ${data.drivingLicence}` },
              ].filter(r => r.show).map((row, i) => (
                <ListItem key={i} disableGutters disablePadding
                  style={{ alignItems: "flex-start", marginBottom: `${Math.round(paraGap * 0.15)}px` }}>
                  <Box style={{ marginRight: "5px", marginTop: "2px", flexShrink: 0 }}>{row.icon}</Box>
                  <Typography style={{ ...sidebarTextStyle, fontSize: "11px", lineHeight: 1.4 }}>{row.val}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case "education": {
        let entries = [];
        try {
          const forResume = localStorage.getItem("educationEntriesForResume");
          if (forResume) {
            entries = JSON.parse(forResume);
          } else if (educationEntries.length) {
            entries = educationEntries.map(e => {
              if (e.educationLevel === "school") {
                return {
                  id: e.id,
                  degree: `${e.standard || ""}${e.standard ? " Standard" : ""}`,
                  fieldOfStudy: e.stream || "General Education",
                  schoolName: e.schoolName || "",
                  schoolLocation: e.schoolLocation || "",
                  gradMonth: "", gradYear: "",
                  additionalCoursework: [
                    e.district,
                    e.percentage && `Percentage: ${e.percentage}%`,
                  ].filter(Boolean).join(" | "),
                };
              }
              return {
                id: e.id,
                degree: `${e.degreeName || ""}${e.programType ? ` (${e.programType})` : ""}`,
                fieldOfStudy: e.specialization || e.degreeType || "",
                schoolName: e.collegeName || "",
                schoolLocation: e.collegeLocation || "",
                gradMonth: e.gradMonth || "",
                gradYear: e.gradYear || "",
                additionalCoursework: [
                  e.cgpa       && `CGPA: ${e.cgpa}`,
                  e.percentage && `Percentage: ${e.percentage}%`,
                  e.grade      && `Grade: ${e.grade}`,
                  e.additionalCoursework,
                ].filter(Boolean).join(" | "),
              };
            });
          }
        } catch {}
        return (
          <Box>
            <Typography style={sidebarHeadStyle}>EDUCATION</Typography>
            {entries.length ? entries.map((edu, i) => (
              <Box key={edu.id || i} style={{ marginBottom: `${paraGap}px` }}>
                <Typography style={{ ...sidebarTextStyle, fontWeight: "bold", fontSize: "11px" }}>
                  {edu.degree}{edu.fieldOfStudy ? ` - ${edu.fieldOfStudy}` : ""}
                </Typography>
                <Typography style={{ ...sidebarTextStyle, fontSize: "10.5px" }}>
                  {edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ""}
                </Typography>
                {(edu.gradMonth || edu.gradYear) && (
                  <Typography style={{ ...sidebarTextStyle, fontStyle: "italic", fontSize: "10px" }}>
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </Typography>
                )}
                {edu.additionalCoursework && (
                  <Typography style={{ ...sidebarTextStyle, fontSize: "10px" }}>
                    {edu.additionalCoursework}
                  </Typography>
                )}
              </Box>
            )) : (
              <Typography style={{ ...sidebarTextStyle, fontSize: "11px" }}>No education entries.</Typography>
            )}
          </Box>
        );
      }

      case "skills":
        return (
          <Box>
            <Typography style={sidebarHeadStyle}>SKILLS</Typography>
            {(savedSkills.length ? savedSkills : data.skills).map((sk, i) => (
              <Box key={i} style={{ marginBottom: `${Math.max(4, Math.round(paraGap * 0.25))}px` }}>
                <Typography style={{ ...sidebarTextStyle, fontSize: "11px" }}>
                  {typeof sk === "string"
                    ? sk
                    : `${sk.name || sk.category || ""}${sk.rating != null ? ` (${sk.rating}%)` : ""}`}
                </Typography>
                {typeof sk !== "string" && sk.rating != null && (
                  <Box style={{ height: "3px", backgroundColor: "rgba(255,255,255,0.25)", borderRadius: "2px", marginTop: "2px" }}>
                    <Box style={{ height: "100%", width: `${sk.rating}%`, backgroundColor: "white", borderRadius: "2px" }} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );

      default:
        return renderRightSection(sectionId, true);
    }
  };

  // ── Right main sections ─────────────────────────────────────────────────
  // sectionId — which section to render
  // inSidebar — whether it's being displayed in the sidebar (different colors)
  // filterIds — if provided, skip this section if its id is not in the list (pagination)
  const renderRightSection = (sectionId, inSidebar = false, filterIds = null) => {
    if (filterIds !== null && !filterIds.includes(sectionId)) return null;

    const tStyle  = inSidebar ? sidebarTextStyle : mainTextStyle;
    const hStyle  = inSidebar ? sidebarHeadStyle : mainHeadStyle;
    const dividerColor = inSidebar ? "rgba(255,255,255,0.3)" : color;

    const SectionBox = ({ children }) => (
      <Box
        className="resume-section"
        data-section-id={sectionId}
        style={sectionBottomStyle}
      >
        {children}
      </Box>
    );

    switch (sectionId) {
      case "profile":
        return (savedSummary || data.profile) ? (
          <SectionBox key="profile">
            <Typography style={hStyle}>PROFILE</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            <Typography style={tStyle}>{savedSummary || data.profile}</Typography>
          </SectionBox>
        ) : null;

      case "workExperience":
        return (
          <SectionBox key="workExperience">
            <Typography style={hStyle}>WORK EXPERIENCE</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {workExperiences && workExperiences.length ? workExperiences.map((w, i) => (
              <Box key={i} style={{ marginBottom: `${paraGap}px` }}>
                <Box style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "2px" }}>
                  <Typography style={{ ...tStyle, fontWeight: "bold", flex: 1, minWidth: 0 }}>
                    {w.jobTitle} @ {w.employer}
                  </Typography>
                  <Typography style={{ ...tStyle, fontSize: "11px", whiteSpace: "nowrap", flexShrink: 0, marginLeft: "8px" }}>
                    {formatDate(w.startMonth, w.startYear)} – {w.current ? "Present" : formatDate(w.endMonth, w.endYear)}
                  </Typography>
                </Box>
                <Typography style={{ ...tStyle, fontStyle: "italic", fontSize: "11px" }}>
                  {w.companyName}{w.location ? ` | ${w.location}` : ""}
                </Typography>
                {w.description && (
                  <Box
                    style={{ marginTop: "4px", ...tStyle, fontSize: "11px" }}
                    dangerouslySetInnerHTML={{
                      __html: w.description
                        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                        .replace(/\*(.*?)\*/g,     "<em>$1</em>")
                        .replace(/\n/g,             "<br/>"),
                    }}
                  />
                )}
              </Box>
            )) : (
              <Typography style={{ ...tStyle, fontSize: "11px" }}>No work experience added.</Typography>
            )}
          </SectionBox>
        );

      case "languages":
        return savedLanguages.length ? (
          <SectionBox key="languages">
            <Typography style={hStyle}>LANGUAGES</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedLanguages.map((l, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                {l.name}{l.level ? ` – ${l.level}` : ""}
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length ? (
          <SectionBox key="accomplishments">
            <Typography style={hStyle}>ACCOMPLISHMENTS</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedAccomplishments.map((a, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                {a}
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      case "certifications":
        return savedCertifications.length ? (
          <SectionBox key="certifications">
            <Typography style={hStyle}>CERTIFICATIONS</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedCertifications.map((c, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                {c.name}{c.provider ? ` – ${c.provider}` : ""}{c.year ? ` (${c.year})` : ""}
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      case "softwareSkills":
        return softwareSkills.length ? (
          <SectionBox key="softwareSkills">
            <Typography style={hStyle}>SOFTWARE SKILLS</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {softwareSkills.map((s, i) => (
              <Box key={i} style={{ marginBottom: `${Math.round(paraGap * 0.25)}px` }}>
                <Typography style={{ ...tStyle, fontSize: "11px" }}>{s.name} — {s.level}%</Typography>
                <Box style={{ height: "3px", backgroundColor: inSidebar ? "rgba(255,255,255,0.25)" : "#e0e0e0", borderRadius: "2px", marginTop: "2px" }}>
                  <Box style={{ height: "100%", width: `${s.level}%`, backgroundColor: color, borderRadius: "2px" }} />
                </Box>
              </Box>
            ))}
          </SectionBox>
        ) : null;

      case "volunteering":
        return savedVolunteering.length ? (
          <SectionBox key="volunteering">
            <Typography style={hStyle}>VOLUNTEERING</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedVolunteering.map((v, i) => (
              <Box key={i} style={{ marginBottom: `${paraGap}px` }}>
                <Typography style={{ ...tStyle, fontWeight: "bold", fontSize: "11px" }}>{v.subtopic}</Typography>
                <Typography style={{ ...tStyle, fontSize: "10px", opacity: 0.75 }}>{v.fromDate} - {v.toDate}</Typography>
                <Typography style={{ ...tStyle, fontSize: "11px" }}>{v.content}</Typography>
              </Box>
            ))}
          </SectionBox>
        ) : null;

      case "interests":
        return savedInterests.length ? (
          <SectionBox key="interests">
            <Typography style={hStyle}>INTERESTS</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            <Box style={{ display: "flex", flexWrap: "wrap", gap: `4px ${Math.round(paraGap * 0.5)}px` }}>
              {savedInterests.map((item, i) => (
                <Typography key={i} style={{ ...tStyle, fontSize: "11px" }}>• {item}</Typography>
              ))}
            </Box>
          </SectionBox>
        ) : null;

      case "websites":
        return savedWebsites.length ? (
          <SectionBox key="websites">
            <Typography style={hStyle}>WEBSITES / PROFILES</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedWebsites.map((s, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                <a
                  href={s.url?.startsWith("http") ? s.url : `https://${s.url}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: inSidebar ? "#90caf9" : "#1976d2", textDecoration: "none", wordBreak: "break-all" }}
                >
                  {s.name || s.url}
                </a>
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      case "references":
        return savedReferences.length ? (
          <SectionBox key="references">
            <Typography style={hStyle}>REFERENCES</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedReferences.map((ref, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                {ref}
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length ? (
          <SectionBox key="additionalInfo">
            <Typography style={hStyle}>ADDITIONAL INFORMATION</Typography>
            <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                {info}
              </Typography>
            ))}
          </SectionBox>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name  = sectionId.replace("custom_", "");
          const items = customSections[name] || [];
          return items.length ? (
            <SectionBox key={sectionId}>
              <Typography style={hStyle}>{name.toUpperCase()}</Typography>
              <Divider style={{ marginBottom: `${Math.round(paraGap * 0.3)}px`, borderColor: dividerColor, borderBottomWidth: lineWeight }} />
              {items.map((item, i) => (
                <Typography key={i} style={{ ...tStyle, fontSize: "11px", marginBottom: `${Math.round(paraGap * 0.2)}px` }}>
                  {item}
                </Typography>
              ))}
            </SectionBox>
          ) : null;
        }
        return null;
    }
  };

  // ── Pagination ──────────────────────────────────────────────────────────
  // Available height for main content (subtract top+bottom padding)
  const availableHeight = page.height - (2 * tbPad);

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const sections = Array.from(mainContentRef.current.querySelectorAll(".resume-section"));
    if (sections.length === 0) { setPages([{ rightIds: [] }]); return; }

    const newPages = [];
    let currentIds = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const id = section.getAttribute("data-section-id");
      const h  = section.offsetHeight;
      if (currentHeight + h > availableHeight && currentIds.length > 0) {
        newPages.push({ rightIds: currentIds });
        currentIds = [id];
        currentHeight = h;
      } else {
        currentIds.push(id);
        currentHeight += h;
      }
    });
    if (currentIds.length > 0) newPages.push({ rightIds: currentIds });
    setPages(newPages);
  }, [
    availableHeight, rightSectionOrder,
    // content deps
    savedSummary, workExperiences, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedReferences, savedAdditionalInfo, customSections,
    // style deps
    fontSize, headingSize, font, fontStyle, lineSpacing, paragraphSpacing, sectionSpacing,
  ]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 400);
    return () => clearTimeout(id);
  }, [paginateContent]);

  const getSectionTitle = id => {
    const map = {
      contact: "Contact", education: "Education", skills: "Skills",
      profile: "Profile", workExperience: "Work Experience", languages: "Languages",
      accomplishments: "Accomplishments", certifications: "Certifications",
      softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles",
      references: "References", additionalInfo: "Additional Info",
    };
    if (id.startsWith("custom_")) return id.replace("custom_", "").toUpperCase();
    return map[id] || id;
  };

  // ── Layout styles ────────────────────────────────────────────────────────
  const pageStyle = {
    width:          `${page.width}px`,
    minHeight:      `${page.height}px`,
    maxWidth:       `${page.width}px`,
    overflow:       "hidden",
    display:        "flex",
    flexDirection:  "row",
    backgroundColor: "#ffffff",
    boxShadow:      exporting ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
    margin:         "0 auto 24px",
    boxSizing:      "border-box",
    fontFamily:     font,
    fontSize:       fs,
  };

  const sidebarStyle = {
    width:           `${SIDEBAR_W}px`,
    minWidth:        `${SIDEBAR_W}px`,
    maxWidth:        `${SIDEBAR_W}px`,
    minHeight:       `${page.height}px`,
    overflow:        "hidden",
    backgroundColor: color,
    color:           "white",
    paddingTop:      `${tbPad}px`,
    paddingBottom:   `${tbPad}px`,
    paddingLeft:     `${Math.max(10, Math.round(sidePad * 0.6))}px`,
    paddingRight:    `${Math.max(10, Math.round(sidePad * 0.6))}px`,
    boxSizing:       "border-box",
    display:         "flex",
    flexDirection:   "column",
    gap:             `${Math.max(8, Math.round(secGap * 0.6))}px`,
    flexShrink:      0,
  };

  const mainAreaStyle = {
    width:         `${MAIN_W}px`,
    minWidth:      0,
    minHeight:     `${page.height}px`,
    paddingTop:    `${tbPad}px`,
    paddingBottom: `${tbPad}px`,
    paddingLeft:   `${sidePad}px`,
    paddingRight:  `${sidePad}px`,
    boxSizing:     "border-box",
    display:       "flex",
    flexDirection: "column",
    overflow:      "hidden",
  };

  // ── Drag dialog panel ────────────────────────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box style={{ flex: 1 }}>
      <Typography variant="h6" style={{ marginBottom: "12px", fontWeight: 600 }}>
        {side === "left" ? "Left (Sidebar)" : "Right (Main)"} — {sections.length} sections
      </Typography>
      <Box style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 4, padding: 8 }}>
        {sections.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => mkDragStart(side, sections)(e, idx)}
            onDragOver={e => handleDragOver(e, idx, side)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx, side)}
            onDragEnd={handleDragEnd}
            style={{
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              backgroundColor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border: dragOverIndex === idx && dragOverSide === side
                ? `2px dashed ${color}`
                : "1px solid #ddd",
              cursor: "grab",
            }}>
            <DragIndicator color="action" style={{ marginRight: 8 }} />
            <Typography style={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
        {!sections.length && (
          <Typography style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" style={{ marginTop: 8 }}
        onClick={() => side === "left"
          ? (setLeftSectionOrder(defaultLeftSectionOrder),  localStorage.setItem("resume1LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder)))
          : (setRightSectionOrder(defaultRightSectionOrder), localStorage.setItem("resume1RightSectionOrder", JSON.stringify(defaultRightSectionOrder)))}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  const totalPages = Math.max(1, pages.length);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box style={{ position: "relative", fontFamily: font }}>
      {/* Controls */}
      {!exporting && (
        <Box style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" size="small" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={resetAll}>
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container — renders ALL right sections to measure heights */}
      <Box
        ref={mainContentRef}
        style={{
          position: "absolute", top: -9999, visibility: "hidden", pointerEvents: "none",
          width: `${MAIN_W}px`,
          paddingLeft: `${sidePad}px`, paddingRight: `${sidePad}px`,
          fontFamily: font, fontSize: fs,
          boxSizing: "border-box",
        }}
      >
        {rightSectionOrder.map(id => (
          <React.Fragment key={id}>{renderRightSection(id, false)}</React.Fragment>
        ))}
      </Box>

      {/* Resume Pages */}
      {(pages.length > 0 ? pages : [{ rightIds: null }]).map((pageContent, pageIdx) => (
        <Box
          key={pageIdx}
          className="resume-page"
          style={{
            ...pageStyle,
            display: exporting ? "flex" : (pageIdx === currentPage ? "flex" : "none"),
          }}
        >
          {/* SIDEBAR — always shown on every page */}
          <Box style={sidebarStyle}>
            {pageIdx === 0 && (
              <Box style={{ textAlign: "center", marginBottom: `${Math.round(paraGap * 0.4)}px` }}>
                <Typography style={{
                  fontFamily: font, fontWeight: 800, fontSize: "20px",
                  lineHeight: lh, color: "white", ...overflowText,
                }}>
                  {(data.firstName || "").toUpperCase()} {(data.lastName || "").toUpperCase()}
                </Typography>
                {data.currentPosition && (
                  <Typography style={{
                    fontSize: "11px", color: "rgba(255,255,255,0.8)",
                    marginTop: "3px", lineHeight: lh, ...overflowText,
                  }}>
                    {data.currentPosition}
                  </Typography>
                )}
              </Box>
            )}
            {pageIdx === 0 && leftSectionOrder.map((id, i) => {
              const content = renderLeftSection(id);
              if (!content) return null;
              return (
                <React.Fragment key={id}>
                  {i > 0 && (
                    <Divider style={{ borderColor: "rgba(255,255,255,0.25)", borderBottomWidth: lineWeight }} />
                  )}
                  {content}
                </React.Fragment>
              );
            })}

          </Box>

          {/* MAIN CONTENT — only sections assigned to this page */}
          <Box style={mainAreaStyle}>
            <Box style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
              {rightSectionOrder.map(id => (
                <React.Fragment key={id}>
                  {renderRightSection(id, false, pageContent.rightIds)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Arrange Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Drag to reorder sections or move them between sidebar and main content area.
          </Typography>
          <Box style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {renderDragPanel("left",  leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume1;